import React, { useState, useEffect, useRef, useMemo } from "react";
import {
    getListingsWithChildren,
    getCalendarBatchData,
    updateCalendarPricesBulk
} from "../services/api";
import usePermissions from '../hooks/usePermissions';
import { PERMISSIONS } from '../constants/permissions';
import {
    Container,
    CalendarWrapper,
    LoadingWrapper,
    HeaderWrapper,
    ButtonGroup,
    CalendarGrid,
    DayCell,
    StatusBadge,
    NoteTooltip,
    ListingsWrapper,
    ListingsColumn,
    ListingsRow,
    ListingImage,
    DayWrap,
    ListingName,
    Listingsub,
    DayCellWrap,
    PlaceholderCell,
    CalH2,
    CalendarScrollableArea,
    ResponsiveDropdownWrapper,
    ErrorP,
    ErrorWarp,
    ErrorImg,
    ToastContainer,
    ToastCloseButton,
    UpdateIndicator,
    SpinnerIcon,
    UpdatingOverlay,
    CellSpinner,
    pulse,
    LoadingText,
    SyncButton,
    SyncButtonIcon,
    ReservationBlockLayer,
    PopupOverlay,
    PopupContent,
    DayCellBlockLabel,
    DayCellLabel,
} from "../style-comps/Calendar.styles";
import runningModel from '../Assets/models/running.glb';
import AirbnbIcon from '../Assets/airbnb-icon.svg';
import ExpediaIcon from '../Assets/Expedia-icon.svg';
import BookingIcon from '../Assets/Booking-icon.svg';
import KozyguruIcon from '../Assets/Kozyguru-Icon.svg';
import AgodaIcon from '../Assets/agoda-icon.png';
import DirectIcon from '../Assets/Kozyguru-Icon.svg';
import { format } from "date-fns";
import { Link } from "react-router-dom";
import Button from "../components/Button";
import { Dropdown } from "../components/Dropdown";
import "@google/model-viewer";
import TooltipWrapper from '../components/TooltipWrapper';
import PopUp from "../components/PopUp";
import SyncIcon from '../Assets/arrow-round-left.svg';



//篩選平台來源
const cleanSource = (rawSource) => {
    if (!rawSource) return "KozyGuru";
    const lower = rawSource.toLowerCase();
    if (lower.includes("expedia")) return "Expedia";
    if (lower.includes("marriott")) return "Marriott";
    if (lower.includes("airbnb")) return "Airbnb";
    if (lower.includes("booking")) return "Booking.com";
    if (lower.includes("vrbo")) return "VRBO";
    if (lower.includes("agoda")) return "Agoda";
    if (lower.includes("direct") || lower.includes("kozyguru")) return "KozyGuru";
    return rawSource.trim();  // 👈 其他沒命中的來源會直接顯示原字串
}

const channelStyles = {
    airbnb: { borderColor: "#F75C60", backgroundColor: "#F75C60" },
    expedia: { borderColor: "#FDDB32", backgroundColor: "#FDDB32" },
    "booking.com": { borderColor: "#003B95", backgroundColor: "#003B95" },
    agoda: { borderColor: "#E52833", backgroundColor: "#E52833" },
    KozyGuru: { borderColor: "#61CBC9", backgroundColor: "#61CBC9" },
    default: { borderColor: "#61CBC9", backgroundColor: "#61CBC9" },
    vrbo: { borderColor: "#FDDB32", backgroundColor: "#FDDB32" },
};

const isToday = (dateString) => {
    const today = new Date().toDateString();
    const checkDate = new Date(dateString).toDateString();
    return today === checkDate;
};

const getPlatformIcon = (source) => {
    switch (source) {
        case "Airbnb":
            return AirbnbIcon;
        case "Expedia":
            return ExpediaIcon;
        case "Booking.com":
            return BookingIcon;
        case "Agoda":
            return AgodaIcon;
        case "KozyGuru":
        case "Direct":
            return KozyguruIcon;
        default:
            return KozyguruIcon;
    }
};



const showTooltip = (content, e) => {
    let tooltip = document.getElementById("global-tooltip");
    if (!tooltip) {
        tooltip = document.createElement("div");
        tooltip.id = "global-tooltip";
        document.body.appendChild(tooltip);
    }

    tooltip.innerText = content;
    tooltip.style.display = 'block';

    const tooltipWidth = tooltip.offsetWidth;
    const tooltipHeight = tooltip.offsetHeight;
    const screenWidth = window.innerWidth;

    // 判斷滑鼠靠不靠右
    const isRightEdge = e.clientX + tooltipWidth + 20 > screenWidth;

    const left = isRightEdge
        ? e.clientX - tooltipWidth - 12
        : e.clientX + 12;

    Object.assign(tooltip.style, {
        position: "fixed",
        top: `${e.clientY - tooltipHeight / 2}px`,
        left: `${left}px`,
        background: "#61CBC9",
        color: "black",
        fontSize: "10px",
        padding: "4px 8px",
        borderRadius: "4px",
        zIndex: 9999,
        pointerEvents: "none",
        whiteSpace: "nowrap",
    });
};


const hideTooltip = () => {
    const tooltip = document.getElementById("global-tooltip");
    if (tooltip) tooltip.remove();
};

const getBatchRange = () => {
    const now = new Date();

    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 2, 0);

    return {
        startDate: format(start, "yyyy-MM-dd"),
        endDate: format(end, "yyyy-MM-dd")
    };
};

// 把某個日期範圍展開成「涵蓋的完整月份」，因為 calendarData 是以月為單位快取
// （key 是 `listingId-YYYY-MM`），抓半個月份的資料無法讓那個月正確顯示。
const expandRangeToFullMonths = (startDate, endDate) => {
    const s = new Date(startDate);
    const e = new Date(endDate);
    return {
        start: new Date(s.getFullYear(), s.getMonth(), 1),
        end: new Date(e.getFullYear(), e.getMonth() + 1, 0),
    };
};

// 存/改價格後要重抓的範圍：預設 3 個月窗口，再聯集上「剛剛被編輯的日期範圍」，
// 避免編輯的是當月 +2 / +3 這種超出預設窗口的月份時，重新整理後資料抓不到。
const getFetchRange = (extraRange) => {
    const base = getBatchRange();
    if (!extraRange?.startDate || !extraRange?.endDate) return base;

    const baseStart = new Date(base.startDate);
    const baseEnd = new Date(base.endDate);
    const { start: extraStart, end: extraEnd } = expandRangeToFullMonths(
        extraRange.startDate,
        extraRange.endDate
    );

    const start = extraStart < baseStart ? extraStart : baseStart;
    const end = extraEnd > baseEnd ? extraEnd : baseEnd;

    return {
        startDate: format(start, "yyyy-MM-dd"),
        endDate: format(end, "yyyy-MM-dd"),
    };
};

const normalizeCalendarDay = (day, listingId) => {
    let status = day.Status || "available";

    if (typeof status === "string") {
        status = status.toLowerCase();
        if (status === "unavailable") status = "blocked";
    }

    return {
        id: day.Id,
        date: day.Date ? day.Date.split("T")[0] : null,
        currency: day.Currency,
        price: Number(day.Price) || 0,
        note: day.Note,
        status,
        listingId: day.ListingId || listingId,
        reservationId: day.ReservationId,
        source: day.Source,
        revenue: day.Revenue,
        nights: day.Nights,
        checkIn: day.CheckIn,
        checkOut: day.CheckOut
    };
};

const buildCalendarAndReservationMaps = (batchResult) => {
    const nextCalendarData = {};
    const nextReservationMap = {};

    const rows = Array.isArray(batchResult?.success) ? batchResult.success : [];

    rows.forEach((item) => {
        const listingId = item.listingId;
        const calendarList = Array.isArray(item.calendar) ? item.calendar : [];
        const reservationById = {};

        calendarList.forEach((day) => {
            const formattedDay = normalizeCalendarDay(day, listingId);
            if (!formattedDay.date) return;

            const date = new Date(formattedDay.date);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const key = `${listingId}-${year}-${month}`;

            if (!nextCalendarData[key]) {
                nextCalendarData[key] = [];
            }

            nextCalendarData[key].push(formattedDay);

            if (
                formattedDay.status === "booked" &&
                day.ReservationId &&
                day.CheckIn &&
                day.CheckOut
            ) {
                reservationById[day.ReservationId] = {
                    Id: day.ReservationId,
                    ListingId: listingId,
                    CheckIn: day.CheckIn,
                    CheckOut: day.CheckOut,
                    Nights: day.Nights,
                    Source: day.Source,
                    Currency: day.Currency,
                    OwnerRevenue: day.Revenue,
                    Status: "accepted"
                };
            }
        });

        nextReservationMap[listingId] = Object.values(reservationById);
    });

    return { nextCalendarData, nextReservationMap };
};


const Calendar = () => {


    const permissions = usePermissions();

    const [currentMonth, setCurrentMonth] = useState(() => {
        const now = new Date();
        now.setDate(1);
        return now;
    });
    const [calendarData, setCalendarData] = useState({});
    const [listingInfoList, setListingInfoList] = useState([]);
    const [reservationMap, setReservationMap] = useState({});
    const [showLoading, setShowLoading] = useState(true);
    const [progress, setProgress] = useState(0);
    const [fadeOut, setFadeOut] = useState(false);
    const [selectedDate, setSelectedDate] = useState(() => {
        const now = new Date();
        now.setDate(1);
        return now.getTime();
    });
    const [openDropdown, setOpenDropdown] = useState(null);
    const [hasNoListings, setHasNoListings] = useState(false);
    const [apiError, setApiError] = useState(null);
    const [smoothProgress, setSmoothProgress] = useState(0);
    const progressRef = useRef(0);
    const [popupData, setPopupData] = useState(null);
    const [showBlockFeature, setShowBlockFeature] = useState(false);
    const [isUpdatingPrice, setIsUpdatingPrice] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [updatingDates, setUpdatingDates] = useState(new Set());
    const [renderKey, setRenderKey] = useState(0);
    const [isLoadingMonth, setIsLoadingMonth] = useState(false);


    const calendarGridRef = useRef(null);
    const userId = localStorage.getItem("userId");


    // 🆕 功能開關
    const ENABLE_EDITING = true;     // 控制編輯功能
    const ENABLE_PRICE_VIEW = true;  // 控制價格顯示
    const ENABLE_BLOCK_FEATURE = showBlockFeature;

    const permissionChecks = {
        canViewCalendar: permissions.hasPermission(PERMISSIONS.CALENDAR_READ),
        canViewPrices: ENABLE_PRICE_VIEW && permissions.hasPermission(PERMISSIONS.LISTINGS_VIEW_PRICES),
        canEditPrices: ENABLE_EDITING && permissions.hasPermission(PERMISSIONS.LISTINGS_EDIT_PRICES),
        canUseBlockFeature: ENABLE_BLOCK_FEATURE,
    };

    const loadBatchCalendarData = async (listings, options = {}) => {
        const { silent = false, extraRange = null } = options;

        if (!listings || listings.length === 0) return;

        setIsRefreshing(true);

        try {
            const { startDate, endDate } = getFetchRange(extraRange);
            const listingIds = listings.map((listing) => listing.Id);

            const perPage = 250;
            let page = 1;
            let keepLoading = true;

            const finalCalendarData = {};
            const finalReservationMap = {};
            const maxPages = 20;

            while (keepLoading && page <= maxPages) {
                const result = await getCalendarBatchData({
                    listingIds,
                    startDate,
                    endDate,
                    perPage,
                    page
                });

                const { nextCalendarData, nextReservationMap } =
                    buildCalendarAndReservationMaps(result);

                Object.entries(nextCalendarData).forEach(([key, days]) => {
                    if (!finalCalendarData[key]) finalCalendarData[key] = [];

                    const existingKeys = new Set(
                        finalCalendarData[key].map((d) => `${d.id}-${d.date}`)
                    );

                    days.forEach((day) => {
                        const uniqueKey = `${day.id}-${day.date}`;
                        if (!existingKeys.has(uniqueKey)) {
                            finalCalendarData[key].push(day);
                            existingKeys.add(uniqueKey);
                        }
                    });
                });

                Object.entries(nextReservationMap).forEach(([listingId, reservations]) => {
                    if (!finalReservationMap[listingId]) finalReservationMap[listingId] = [];

                    const existingIds = new Set(
                        finalReservationMap[listingId].map((r) => r.Id)
                    );

                    reservations.forEach((res) => {
                        if (!existingIds.has(res.Id)) {
                            finalReservationMap[listingId].push(res);
                            existingIds.add(res.Id);
                        }
                    });
                });

                const rows = Array.isArray(result?.success) ? result.success : [];

                keepLoading = rows.some((item) => {
                    const calendarList = Array.isArray(item.calendar) ? item.calendar : [];
                    return calendarList.length === perPage;
                });

                page += 1;
            }

            // 用「月份 key 整批替換」的方式合併，而不是整個覆蓋 calendarData：
            // 這批重新抓到的月份用新資料整批換掉（確保剛改的價格生效），
            // 但先前透過切換月份載入、這次沒抓到的月份要保留，不能被洗掉。
            const mergedCalendarData = { ...calendarData, ...finalCalendarData };
            const mergedReservationMap = { ...reservationMap, ...finalReservationMap };

            setCalendarData(mergedCalendarData);
            setReservationMap(mergedReservationMap);

            localStorage.setItem("cachedCalendarData", JSON.stringify(mergedCalendarData));
            localStorage.setItem("cachedReservationMap", JSON.stringify(mergedReservationMap));
            localStorage.setItem("calendarCacheTime", Date.now().toString());

            setRenderKey((prev) => prev + 1);
        } catch (error) {
            console.error("❌ Batch Calendar API failed:", error);

            if (!silent) {
                setApiError("Failed to load calendar data.");
            } else {
                setToastMessage("Price updated, but calendar refresh failed. Please refresh again.");
                setShowToast(true);
                setTimeout(() => setShowToast(false), 3000);
            }
        } finally {
            setIsRefreshing(false);
        }
    };

    const fetchListings = async () => {
        try {
            setProgress(0);
            setSmoothProgress(3);

            const token = localStorage.getItem("authToken");
            setProgress(10);

            const response = await getListingsWithChildren(userId);
            const listings = response?.Listings || [];

            if (!listings || listings.length === 0) {
                setApiError("You don't have any property listings.");
                setHasNoListings(true);
                setShowLoading(false);
                return;
            }

            const parentOnly = listings.filter((l) => l.ParentListingId === null && l.IsServicePms === true);

            if (!parentOnly.length) {
                setHasNoListings(true);
                setShowLoading(false);
                return;
            }

            setListingInfoList(parentOnly);
            setHasNoListings(false);
            localStorage.setItem("cachedListingInfoList", JSON.stringify(parentOnly));

            setProgress(35);

            const fakeInterval = setInterval(() => {
                setProgress(prev => {
                    if (prev >= 75) {
                        clearInterval(fakeInterval);
                        return prev;
                    }
                    return prev + 1;
                });
                progressRef.current = Math.min(progressRef.current + 1, 75);
            }, 100);

            await loadBatchCalendarData(parentOnly);
            clearInterval(fakeInterval);
            setProgress(100);
            progressRef.current = 100;
            setFadeOut(true);
            setTimeout(() => setShowLoading(false), 500);
        } catch (error) {
            console.error("❌ 無法取得 Listing 或 Calendar 資料：", error);
            setApiError("Failed to load calendar data.");
            setHasNoListings(true);
            setShowLoading(false);
        }
    };

    useEffect(() => {
        const token = localStorage.getItem("authToken");
        if (!token) {
            alert("Your session has expired, please login again to continue");
            localStorage.clear();
            window.location.href = "/login";
            return;
        }


        // 🆕 每次重新載入都清除舊快取並重新抓取 API 資料
        console.log("🔄 清除舊快取，重新載入最新資料...");
        localStorage.removeItem("cachedListingInfoList");
        localStorage.removeItem("cachedCalendarData");
        localStorage.removeItem("cachedReservationMap");

        // 直接呼叫 fetchListings 重新抓取資料
        fetchListings();
    }, []);



    useEffect(() => {
        if (showLoading) {
            const interval = setInterval(() => {
                setSmoothProgress(prev => {
                    const target = Math.min(progressRef.current, 99);
                    return prev < target ? prev + 1 : prev;
                });
            }, 30);

            return () => clearInterval(interval);
        }
    }, [showLoading]);

    useEffect(() => {
        progressRef.current = progress;
    }, [progress]);

    // 同步 currentMonth 和 selectedDate
    useEffect(() => {
        const currentMonthTimestamp = new Date(
            currentMonth.getFullYear(),
            currentMonth.getMonth(),
            1
        ).getTime();

        // 只在兩者不一致時才更新，避免無限循環
        if (selectedDate !== currentMonthTimestamp) {
            setSelectedDate(currentMonthTimestamp);
        }
    }, [currentMonth]);



    useEffect(() => {
        if (!showLoading && currentMonth && !selectedDate) {
            const firstDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
            setSelectedDate(firstDay.getTime());
        }
    }, [showLoading, currentMonth]);

    // 🆕 監聽價格更新事件
    useEffect(() => {
        const handlePriceUpdate = async (event) => {
            const { listingId: updatedListingId, startDate, endDate } = event.detail;

            console.log('📣 Calendar 收到價格更新通知:', event.detail);

            // 清除所有相關的 cache
            console.log('🗑️ 清除快取...');
            localStorage.removeItem("cachedCalendarData");
            localStorage.removeItem("cachedListingInfoList");
            localStorage.removeItem("cachedReservationMap");

            // 重新載入行事曆資料 — 帶上被改到的日期範圍，
            // 避免編輯的月份超出預設 3 個月窗口時抓不回來。
            console.log('🔄 重新載入行事曆資料...');
            await loadBatchCalendarData(listingInfoList, {
                extraRange: startDate && endDate ? { startDate, endDate } : null,
            });

            console.log('✅ Calendar 資料已更新');
        };

        // 註冊監聽
        window.addEventListener('calendarPriceUpdated', handlePriceUpdate);

        // 清除監聽（元件卸載時）
        return () => {
            window.removeEventListener('calendarPriceUpdated', handlePriceUpdate);
        };
    }, []); // 空依賴陣列，只在元件掛載時註冊一次

    // 手機上點別的地方要自動關掉 tooltip（沿用 CalendarSingle.js 已驗證的做法）。
    // 訂單色條本身的 onTouchStart 會呼叫 stopPropagation()，所以不會被這裡立刻關掉。
    useEffect(() => {
        const clearOnTouch = () => hideTooltip();
        document.addEventListener('touchstart', clearOnTouch);
        return () => document.removeEventListener('touchstart', clearOnTouch);
    }, []);

    const dates = useMemo(() => {
        const now = new Date();
        const minAllowed = new Date("2025-06-01");
        const result = [];
        for (let i = -3; i <= 3; i++) {
            const date = new Date(now.getFullYear(), now.getMonth() + i, 1);
            if (date >= minAllowed) {
                result.push({
                    value: date.getTime(),
                    label: format(date, "MMMM yyyy"),
                });
            }
        }
        return result;
    }, []);

    const isAllowedMonth = (date) => {
        const now = new Date();
        const start = new Date(now.getFullYear(), now.getMonth() - 3, 1);
        const end = new Date(now.getFullYear(), now.getMonth() + 4, 0);
        return date >= start && date <= end;
    };

    if (!permissionChecks.canViewCalendar) {
        return (
            <Container>
                <div style={{ padding: '20px', textAlign: 'center' }}>
                    <h2>Access Denied</h2>
                    <p>You do not have permission to view the calendar.</p>
                </div>
            </Container>
        );
    }


    const getMonthDays = (listingId) => {
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth();
        const key = `${listingId}-${year}-${(month + 1).toString().padStart(2, "0")}`;
        const daysData = calendarData[key] || [];

        console.log(`📅 Getting days for ${key}:`, daysData.length, "days");

        const daysInMonth = new Date(year, month + 1, 0).getDate();

        const fullMonthDays = [];
        for (let day = 1; day <= daysInMonth; day++) {
            const dateStr = `${year}-${(month + 1).toString().padStart(2, "0")}-${day.toString().padStart(2, "0")}`;


            const existingDay = daysData.find(d => {
                const dayDate = typeof d.date === 'string' ? d.date.split('T')[0] : d.date;
                return dayDate === dateStr;
            });

            if (existingDay) {
                fullMonthDays.push({
                    id: existingDay.id,
                    date: existingDay.date,
                    status: existingDay.status,
                    currency: existingDay.currency,
                    price: existingDay.price,
                    note: existingDay.note,
                });
            } else {
                fullMonthDays.push({
                    id: `${listingId}-${dateStr}`,
                    date: dateStr,
                    status: "available",
                    currency: "AUD",
                    price: null,
                    note: null,
                });
            }
        }

        return fullMonthDays;
    };

    const formatCurrencySymbol = (currency) => {
        switch (currency) {
            case 'AUD': return '$';
            case 'GBP': return '£';
            case 'USD': return '$';
            default: return '$';
        }
    };


    const renderReservationBlocks = (listingId) => {
        const reservations = reservationMap[listingId] || [];
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth();
        const daysInMonth = new Date(year, month + 1, 0).getDate();

        return reservations.map((res, idx) => {
            const checkIn = new Date(res.CheckIn);
            const checkOut = new Date(checkIn);
            checkOut.setDate(checkOut.getDate() + res.Nights);

            if (checkOut <= new Date(year, month, 1) || checkIn >= new Date(year, month + 1, 1)) return null;

            const cleanedSource = cleanSource(res.Source);
            const style = channelStyles[cleanedSource.toLowerCase()] || channelStyles.default;
            const icon = getPlatformIcon(cleanedSource);

            const startDay = Math.max(1, checkIn.getMonth() === month ? checkIn.getDate() : 1);
            const endDay = Math.min(daysInMonth, checkOut.getMonth() === month ? checkOut.getDate() : daysInMonth);
            const left = (startDay - 1) * 61;
            const width = Math.max((endDay - startDay) * 60, 10);


            return (
                <div
                    key={`res-${res.Id}-${idx}`}
                    style={{
                        position: "absolute",
                        left: `${left}px`,
                        width: `${width}px`,
                        top: "20px",
                        height: "20px",
                        backgroundColor: style.backgroundColor,
                        borderRadius: "16px",
                        color: "white",
                        fontWeight: 500,
                        fontSize: "12px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "0 4px",
                        gap: "4px",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        pointerEvents: width <= 0 ? "none" : "auto",
                        zIndex: 20,
                        position: "absolute",
                        cursor: "pointer",
                        marginLeft: '4px',
                        overflowX: "hidden"
                    }}
                    onMouseEnter={(e) =>
                        showTooltip(
                            `${cleanedSource} | ${res.Nights} nights | ${formatCurrencySymbol(res.Currency)}${res.OwnerRevenue}\nCheck-in: ${format(new Date(res.CheckIn), "yyyy-MM-dd")} | Check-out: ${format(
                                new Date(new Date(res.CheckIn).setDate(new Date(res.CheckIn).getDate() + res.Nights)),
                                "yyyy-MM-dd"
                            )}`,
                            e
                        )
                    }
                    onMouseMove={(e) =>
                        showTooltip(
                            `${cleanedSource} | ${res.Nights} nights\n${formatCurrencySymbol(res.Currency)}${res.OwnerRevenue}\nCheck-in: ${format(new Date(res.CheckIn), "yyyy-MM-dd")}\nCheck-out: ${format(
                                new Date(new Date(res.CheckIn).setDate(new Date(res.CheckIn).getDate() + res.Nights)),
                                "yyyy-MM-dd"
                            )}`,
                            e
                        )
                    }
                    onTouchStart={(e) => {
                        // 手機沒有真正的 hover，靠 tap 顯示 tooltip；stopPropagation 避免
                        // 被 document 的 touchstart 監聽器（上面新增的）立刻關掉。
                        showTooltip(
                            `${cleanedSource} | ${res.Nights} nights\n${formatCurrencySymbol(res.Currency)}${res.OwnerRevenue}\nCheck-in: ${format(new Date(res.CheckIn), "yyyy-MM-dd")}\nCheck-out: ${format(
                                new Date(new Date(res.CheckIn).setDate(new Date(res.CheckIn).getDate() + res.Nights)),
                                "yyyy-MM-dd"
                            )}`,
                            e.touches[0]
                        );
                        e.stopPropagation();
                    }}


                    onMouseLeave={hideTooltip}
                >
                    <img src={icon} alt={cleanedSource} style={{ width: 12, height: 12 }} />
                    <span>{cleanedSource}</span>
                </div>
            );
        });
    };




    const handleDropdownChange = (timestamp) => {
        const newDate = new Date(timestamp);
        if (isAllowedMonth(newDate)) {
            setSelectedDate(timestamp);
            setCurrentMonth(newDate);
            ensureMonthData(newDate);
        }
    };

    const changeMonth = async (offset) => {
        const newDate = new Date(currentMonth);
        newDate.setMonth(currentMonth.getMonth() + offset);
        newDate.setDate(1);
        if (!isAllowedMonth(newDate)) return;
        setCurrentMonth(newDate);
        await ensureMonthData(newDate);
    };

    // 該月份沒有快取資料時補打 API（箭頭與下拉選單共用）
    const ensureMonthData = async (newDate) => {
        const year = newDate.getFullYear();
        const month = String(newDate.getMonth() + 1).padStart(2, '0');
        const hasData = listingInfoList.some(l =>
            (calendarData[`${l.Id}-${year}-${month}`]?.length > 0)
        );

        if (!hasData) {
            try {
                setIsLoadingMonth(true);
                const mStart = format(new Date(newDate.getFullYear(), newDate.getMonth(), 1), "yyyy-MM-dd");
                const mEnd = format(new Date(newDate.getFullYear(), newDate.getMonth() + 1, 0), "yyyy-MM-dd");
                const listingIds = listingInfoList.map(l => l.Id);

                const newCalendarData = { ...calendarData };
                const newReservationMap = { ...reservationMap };

                let page = 1;
                let keepLoading = true;
                while (keepLoading) {
                    const batchResponse = await getCalendarBatchData({
                        listingIds,
                        startDate: mStart,
                        endDate: mEnd,
                        perPage: 250,
                        page
                    });

                    const batchList = batchResponse?.success || batchResponse?.Success || [];
                    if (batchList.length === 0) break;

                    const { nextCalendarData, nextReservationMap } = buildCalendarAndReservationMaps(batchResponse);

                    Object.entries(nextCalendarData).forEach(([key, days]) => {
                        if (!newCalendarData[key]) newCalendarData[key] = [];
                        const existingKeys = new Set(newCalendarData[key].map(d => `${d.id}-${d.date}`));
                        days.forEach(day => {
                            const uniqueKey = `${day.id}-${day.date}`;
                            if (!existingKeys.has(uniqueKey)) {
                                newCalendarData[key].push(day);
                                existingKeys.add(uniqueKey);
                            }
                        });
                    });

                    Object.entries(nextReservationMap).forEach(([listingId, reservations]) => {
                        if (!newReservationMap[listingId]) newReservationMap[listingId] = [];
                        const existingIds = new Set(newReservationMap[listingId].map(r => r.Id));
                        reservations.forEach(res => {
                            if (!existingIds.has(res.Id)) {
                                newReservationMap[listingId].push(res);
                                existingIds.add(res.Id);
                            }
                        });
                    });

                    keepLoading = batchList.some(item => (item.calendar || item.Calendar || []).length === 250);
                    page++;
                }

                setCalendarData(newCalendarData);
                setReservationMap(newReservationMap);
            } catch (err) {
                console.error("❌ 補抓月份 Calendar 失敗", err);
            } finally {
                setIsLoadingMonth(false);
            }
        }
    };







    const dayWidth = 50;

    // 新增:生成當月完整的日期陣列,不依賴特定 listing
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const monthDays = [];
    for (let day = 1; day <= daysInMonth; day++) {
        const dateStr = `${year}-${(month + 1).toString().padStart(2, "0")}-${day.toString().padStart(2, "0")}`;
        monthDays.push({
            date: dateStr,
        });
    }

    const totalDays = monthDays.length;
    const totalGridWidth = totalDays * dayWidth + 200 + 80;

    if (!showLoading && apiError) {
        return (
            <ErrorWarp>
                <ErrorImg src="../Assets/mrK05.png" />
                <ErrorP>{apiError}</ErrorP>
            </ErrorWarp>
        );
    }

    if (hasNoListings) {
        return (
            <ErrorWarp>
                <ErrorImg src="../Assets/mrK05.png" />
                <ErrorP>Sorry, You Don't have any Property Listing</ErrorP>
            </ErrorWarp>
        );
    }
    if (showLoading) {
        return (
            <LoadingWrapper style={{ opacity: fadeOut ? 0 : 1, transition: "opacity 0.6s ease" }}>
                <LoadingText>
                    KozyGuring... {Math.min(smoothProgress, 100)}%
                </LoadingText>

                <model-viewer
                    src={runningModel}
                    autoplay
                    animation-name="Run"
                    camera-controls
                />
            </LoadingWrapper>
        );
    }

    // 把 yyyy-MM-dd 轉 Date（避免時區問題）
    const parseYMD = (ymd) => {
        const [y, m, d] = ymd.split("-").map(Number);
        return new Date(y, m - 1, d);
    };

    // 只改前端的價格（不打 API）
    const applyMockPriceUpdate = (listingId, startYMD, endYMD, newPrice, weekdays = []) => {
        const start = parseYMD(startYMD);
        const end = parseYMD(endYMD);
        if (end < start) return;

        // 深拷貝 calendarData
        const updated = { ...calendarData };
        const today = new Date();
        const today0 = new Date(today.getFullYear(), today.getMonth(), today.getDate());

        // 走訪日期區間
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
            const weekday = d.getDay();
            if (weekdays.length > 0 && !weekdays.includes(weekday)) continue;

            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, "0");
            const key = `${listingId}-${y}-${m}`;
            const days = updated[key];
            if (!days) continue;

            updated[key] = days.map((day) => {
                const dayDate = new Date(day.date);
                const day0 = new Date(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate());

                const isSameDay =
                    day0.getFullYear() === d.getFullYear() &&
                    day0.getMonth() === d.getMonth() &&
                    day0.getDate() === d.getDate();

                if (isSameDay && day.status !== "booked" && day0 >= today0) {
                    return { ...day, price: newPrice };
                }
                return day;
            });
        }


        setCalendarData(updated);
        // 同步更新快取，避免 F5 又回舊資料
        localStorage.setItem("cachedCalendarData", JSON.stringify(updated));
    };

    const applyMockBlockDate = (listingId, startYMD, endYMD) => {
        const start = parseYMD(startYMD);
        const end = parseYMD(endYMD);
        if (end < start) {
            return { success: false, message: "End date cannot be earlier than start date." };
        }

        const updated = { ...calendarData };
        const today = new Date();
        const today0 = new Date(today.getFullYear(), today.getMonth(), today.getDate());

        // 🔥 從 localStorage 獲取原始價格數據作為參考
        const cachedCalendar = localStorage.getItem("cachedCalendarData");
        const originalData = cachedCalendar ? JSON.parse(cachedCalendar) : {};

        // STEP 1 - 找出需要處理的所有月份
        const monthsToUpdate = new Set();
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, "0");
            const key = `${listingId}-${y}-${m}`;
            monthsToUpdate.add(key);
        }

        // STEP 2 - 對每個月份進行處理，清除所有 blocked 狀態並恢復原價
        monthsToUpdate.forEach(key => {
            const days = updated[key];
            if (!days) return;

            // 先將這個月份內所有 blocked 的日期改回 available，並恢復原價
            updated[key] = days.map(day => {
                const dayDate = new Date(day.date);
                const day0 = new Date(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate());

                // 只清除未來的 blocked 日期
                if (day.status === "blocked" && day0 >= today0) {
                    // 🔥 從原始數據中找回該日期的原始價格
                    const originalDays = originalData[key] || [];
                    const originalDay = originalDays.find(d => d.date === day.date);

                    return {
                        ...day,
                        status: "available",
                        price: originalDay?.price ?? day.price,  // 恢復原價
                        currency: originalDay?.currency ?? day.currency
                    };
                }
                return day;
            });
        });

        // STEP 3 - 設定新的 Block 區間
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, "0");
            const key = `${listingId}-${y}-${m}`;

            if (!updated[key]) continue;

            const idx = updated[key].findIndex(day => {
                const dd = new Date(day.date);
                return dd.getFullYear() === d.getFullYear() &&
                    dd.getMonth() === d.getMonth() &&
                    dd.getDate() === d.getDate();
            });

            if (idx === -1) continue;

            const dayDate = new Date(updated[key][idx].date);
            const day0 = new Date(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate());

            // 只 block 未來的日期，且不是 booked 的
            if (day0 >= today0 && updated[key][idx].status !== "booked") {
                updated[key][idx] = { ...updated[key][idx], status: "blocked" };
            }
        }

        setCalendarData(updated);
        localStorage.setItem("cachedCalendarData", JSON.stringify(updated));




        return { success: true };


    };

    // 🆕 處理價格更新的實際 API 呼叫
    const updateCalendarPricesAPI = async (listingId, startDate, endDate, newPrice, weekdays, weekdayPrices, bookedDates = []) => {
        try {
            // 檢查權限
            if (!permissionChecks.canEditPrices) {
                throw new Error("You do not have permission to edit prices.");
            }

            const start = new Date(startDate);
            const end = new Date(endDate);
            const calendar = [];
            const bookedDatesSet = new Set(bookedDates || []);
            console.log("🚫 已預訂的日期:", Array.from(bookedDatesSet));

            const isNewFormat = weekdayPrices && Array.isArray(weekdayPrices);


            const datesToUpdate = new Set();
            if (isNewFormat) {
                // 新格式：按星期幾設定不同價格
                let priceMap = {};
                weekdayPrices.forEach(({ weekday, newPrice }) => {
                    priceMap[weekday] = Number(newPrice);
                });

                console.log("🗺️ 價格對應表:", priceMap);

                const current = new Date(start);
                const addedDates = new Set(); // 🆕 用來防止重複添加相同日期

                while (current <= end) {
                    const weekday = current.getDay();
                    const dateStr = current.toISOString().split('T')[0];

                    console.log(`🔍 檢查日期: ${dateStr}, 星期${weekday}, 是否有價格: ${priceMap[weekday] !== undefined}, 是否 booked: ${bookedDatesSet.has(dateStr)}`);

                    if (priceMap[weekday] !== undefined && !addedDates.has(dateStr) && !bookedDatesSet.has(dateStr)) {
                        calendar.push({
                            startDate: dateStr,
                            endDate: dateStr,
                            price: priceMap[weekday]
                        });
                        addedDates.add(dateStr);
                        datesToUpdate.add(dateStr);
                        console.log(`✅ 添加日期: ${dateStr}, 價格: ${priceMap[weekday]}`);
                    } else if (bookedDatesSet.has(dateStr)) {
                        console.log(`⚠️ 跳過已預訂日期: ${dateStr}`);
                    }

                    current.setDate(current.getDate() + 1);
                }

                console.log("📊 最終 calendar 陣列:", calendar);

            } else {
                // 舊格式：指定星期幾使用同一價格
                if (weekdays && weekdays.length > 0) {
                    const current = new Date(start);
                    while (current <= end) {
                        const weekday = current.getDay();
                        const dateStr = current.toISOString().split('T')[0];

                        if (weekdays.includes(weekday) && !bookedDatesSet.has(dateStr)) {
                            calendar.push({
                                startDate: dateStr,
                                endDate: dateStr,
                                price: Number(newPrice)
                            });
                            datesToUpdate.add(dateStr);
                        } else if (bookedDatesSet.has(dateStr)) {
                            console.log(`⚠️ 跳過已預訂日期: ${dateStr}`);
                        }
                        current.setDate(current.getDate() + 1);
                    }
                } else {
                    // 如果沒有指定星期幾，則更新整個日期範圍
                    const current = new Date(start);
                    while (current <= end) {
                        const dateStr = current.toISOString().split('T')[0];

                        if (!bookedDatesSet.has(dateStr)) {
                            calendar.push({
                                startDate: dateStr,
                                endDate: dateStr,
                                price: Number(newPrice)
                            });
                            datesToUpdate.add(dateStr);
                        } else {
                            console.log(`跳過已預訂日期: ${dateStr}`);
                        }
                        current.setDate(current.getDate() + 1);
                    }
                }
            }
            // 如果沒有指定星期幾，則更新整個日期範圍

            if (calendar.length === 0) {
                throw new Error("No dates available to update.");
            }

            // 準備 API 請求格式
            const priceUpdates = [{
                listingId: Number(listingId),
                calendar: calendar
            }];

            console.log("發送價格更新請求:");
            console.log("更新數量:", calendar.length, "個日期");
            console.log("請求資料:", JSON.stringify(priceUpdates, null, 2));

            // 標記這些日期為「更新中」
            setUpdatingDates(datesToUpdate);

            // Call API
            const result = await updateCalendarPricesBulk(priceUpdates);

            // Wait 1s for backend to sync before refetching
            await new Promise(resolve => setTimeout(resolve, 1000));

            // Reload calendar data — 帶上剛剛實際被改到的日期範圍，
            // 確保就算編輯的是當月 +2 / +3 這種超出預設 3 個月窗口的月份，
            // 存檔後也會被重新抓回來顯示（每一間房源都會抓）。
            await loadBatchCalendarData(listingInfoList, {
                silent: true,
                extraRange: { startDate, endDate },
            });

            // Clear updating state
            setUpdatingDates(new Set());

            return { success: true, updatedCount: calendar.length, result };
        } catch (error) {
            console.error("價格更新失敗:", error);

            // 🔥 清除更新標記
            setUpdatingDates(new Set());

            throw error;
        }
    };


    return (
        <Container style={{ position: 'relative' }}>
            {isLoadingMonth && (
                <div style={{
                    position: 'fixed',
                    top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(255,255,255,0.6)',
                    zIndex: 99999,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}>
                    <p style={{ fontSize: '18px', color: '#61CBC9', fontWeight: 'bold' }}>
                        Loading...
                    </p>
                </div>
            )}
            <HeaderWrapper>
                <CalH2>{currentMonth.toLocaleDateString("en-US", { year: "numeric", month: "long" })}</CalH2>

                {/* 🆕 Block Tab 顯示開關 */}
                {/* <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px"
                }}>
                    <label style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        fontSize: "14px",
                        color: "#666",
                        cursor: "pointer",
                        userSelect: "none"
                    }}>
                        <input
                            type="checkbox"
                            checked={showBlockFeature}
                            onChange={(e) => setShowBlockFeature(e.target.checked)}
                            style={{
                                width: "16px",
                                height: "16px",
                                accentColor: "#61CBC9"
                            }}
                        />
                        顯示 Block Date Tab
                    </label>
                </div> */}


                <ButtonGroup>
                    <Button label="Today" onClick={() => {
                        const today = new Date();
                        today.setDate(1);
                        setCurrentMonth(today);
                    }}
                        style={{ backgroundColor: "#61cbc9", color: "#fff", borderRadius: "8px" }} />
                    <ResponsiveDropdownWrapper>
                        <Dropdown
                            options={dates}
                            defaultLabel="Select Month"
                            value={selectedDate}
                            onChange={handleDropdownChange}
                            menuPortalTarget={document.body}
                            menuPosition="absolute"
                            isOpen={openDropdown === 'date'}
                            onOpen={() => setOpenDropdown('date')}
                            onClose={() => setOpenDropdown(null)}
                            styles={{
                                container: (base) => ({
                                    ...base,
                                    zIndex: 1000,
                                    width: '180px',      // 固定容器寬度
                                    minWidth: '180px'    // 最小寬度
                                }),
                                control: (base) => ({
                                    ...base,
                                    zIndex: 1000,
                                    width: '180px',      // 固定控制項寬度
                                    minWidth: '180px'    //  最小寬度
                                }),
                                valueContainer: (base) => ({
                                    ...base,
                                    width: '140px',        // 固定寬度
                                    minWidth: '140px',     // 最小寬度
                                    maxWidth: '140px',     // 最大寬度 新增
                                    flexWrap: 'nowrap',    // 不換行 新增
                                    overflow: 'hidden',    // 隱藏溢出 新增
                                }),
                                menuPortal: (base) => ({
                                    ...base,
                                    zIndex: 99999,
                                    position: "absolute"
                                }),
                                menu: (base) => ({
                                    ...base,
                                    zIndex: 99999,
                                    width: '180px',      //  固定下拉選單寬度
                                }),
                                singleValue: (base) => ({
                                    ...base,
                                    overflow: 'hidden',           // 隱藏溢出
                                    textOverflow: 'ellipsis',     // 用 ... 表示截斷
                                    whiteSpace: 'nowrap',         // 不換行
                                    maxWidth: '100%',             // 最大寬度 100%
                                }),
                                input: (base) => ({
                                    ...base,
                                    margin: 0,    // 移除邊距
                                    padding: 0,   // 移除內距
                                }),
                                indicatorsContainer: (base) => ({
                                    ...base,
                                    width: '40px',    // 固定箭頭區域寬度
                                })
                            }}
                        />
                    </ResponsiveDropdownWrapper>
                </ButtonGroup>
                <ButtonGroup>
                    <Button
                        label="<"
                        onClick={() => changeMonth(-1)}
                        disabled={!isAllowedMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                        style={{ backgroundColor: "#61cbc9", color: "#fff", borderRadius: "8px" }}
                    />
                    <Button
                        label=">"
                        onClick={() => changeMonth(1)}
                        disabled={!isAllowedMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                        style={{ backgroundColor: "#61cbc9", color: "#fff", borderRadius: "8px" }}
                    />
                    <SyncButton
                        isRefreshing={isRefreshing}
                        onClick={async () => {
                            setIsRefreshing(true);
                            await loadBatchCalendarData(listingInfoList);
                            setToastMessage("Calendar refreshed successfully!");
                            setShowToast(true);
                            setTimeout(() => setShowToast(false), 3000);
                        }}
                        disabled={isRefreshing}
                        title="Refresh Calendar"
                    >
                        <SyncButtonIcon
                            src={SyncIcon}
                            alt="Refresh Icon"
                            isRefreshing={isRefreshing}
                        />
                    </SyncButton>
                </ButtonGroup>


            </HeaderWrapper>

            <CalendarWrapper>
                <ListingsWrapper>
                    <ListingsColumn>
                        {listingInfoList.map((listing) => (
                            <ListingsRow key={listing.Id}>
                                <ListingImage src={listing.Thumbnail || "/images/default.png"} />
                                <DayWrap>
                                    {listing.IsServicePms === false ? (
                                        // Service Off：純文字，不能點
                                        <ListingName as="span" style={{ cursor: "default", color: "#aaa" }}>
                                            {listing.KozyGuruName}
                                        </ListingName>
                                    ) : (
                                        <ListingName as={Link} to={`/dashboard/calendar/${listing.Id}`}>
                                            {listing.KozyGuruName}
                                        </ListingName>
                                    )}
                                    <Listingsub>{listing.Street}</Listingsub>
                                    {listing.IsServicePms === false && (
                                        <span style={{
                                            display: "inline-block",
                                            marginTop: 2,
                                            padding: "2px 8px",
                                            borderRadius: "999px",
                                            backgroundColor: "#e0e0e0",
                                            color: "#888",
                                            fontSize: "10px",
                                            fontWeight: 600,
                                        }}>
                                            Service Off
                                        </span>
                                    )}
                                </DayWrap>
                            </ListingsRow>
                        ))}
                    </ListingsColumn>

                    <CalendarScrollableArea ref={calendarGridRef}>
                        <div style={{ width: totalGridWidth }}>
                            <div style={{ display: "flex" }}>
                                {monthDays.map((day, index) => (
                                    <DayCellWrap
                                        key={index}
                                        isToday={isToday(day.date)}
                                        isWeekend={["Sat", "Sun"].includes(format(new Date(day.date), "EEE"))}>
                                        {day.date && (
                                            <>
                                                <span className="date">{format(new Date(day.date), "dd")}</span>
                                                <span className="weekday">{format(new Date(day.date), "EEE")}</span>
                                            </>
                                        )}
                                    </DayCellWrap>
                                ))}
                            </div>

                            {listingInfoList.map((listing) => {
                                const days = getMonthDays(listing.Id);
                                return (
                                    <div key={`${listing.Id}-${renderKey}`} style={{ position: "relative", height: "60px" }}>
                                        <div style={{ display: "flex", position: "relative", zIndex: 2 }}>
                                            {days.map((day) => {
                                                const year = currentMonth.getFullYear();
                                                const month = currentMonth.getMonth();
                                                const dateStr = day.date;
                                                const targetDate = new Date(dateStr);
                                                const reservations = (reservationMap[listing.Id] || []).filter((res) => {
                                                    const checkIn = new Date(res.CheckIn);
                                                    const checkOut = new Date(checkIn);
                                                    checkOut.setDate(checkOut.getDate() + res.Nights);
                                                    return targetDate >= checkIn && targetDate < checkOut;
                                                });

                                                const isBooked = reservations.length > 0 || day.status === "booked";
                                                const firstReservation = reservations[0];
                                                const cleanedSource = firstReservation ? cleanSource(firstReservation.Source) : null;
                                                const style = cleanedSource
                                                    ? channelStyles[cleanedSource.toLowerCase()] || channelStyles.default
                                                    : null;

                                                const today = new Date();
                                                const isFuture = targetDate >= new Date(today.getFullYear(), today.getMonth(), today.getDate());
                                                const isPast = targetDate < new Date(today.getFullYear(), today.getMonth(), today.getDate());

                                                return (
                                                    <DayCell
                                                        key={`${listing.Id}-${day.id}`}
                                                        status={day.status}
                                                        isToday={isToday(day.date)}
                                                        isWeekend={["Sat", "Sun"].includes(format(new Date(day.date), "EEE"))}
                                                        onClick={permissionChecks.canEditPrices ? () => {
                                                            if (listing.IsServicePms === false) return;
                                                            if (!isFuture) return;
                                                            if (!permissionChecks.canEditPrices) {
                                                                setToastMessage("You do not have permission to edit the price");
                                                                setShowToast(true);
                                                                setTimeout(() => setShowToast(false), 5000);
                                                                return;
                                                            }

                                                            if (isBooked) {
                                                                setToastMessage("This date is already booked and cannot be edited.");
                                                                setShowToast(true);
                                                                setTimeout(() => setShowToast(false), 5000);
                                                                return;
                                                            }


                                                            // CASE 1：BLOCKED DATE → 編輯 Block 區間

                                                            if (day.status === "blocked" && showBlockFeature) {

                                                                // 取同 listing、同月份全部 Day 資料
                                                                const monthKey = `${listing.Id}-${year}-${String(month + 1).padStart(2, '0')}`;
                                                                const monthDays = calendarData[monthKey] || [];

                                                                const clickedDate = new Date(day.date);

                                                                // ---------- 找出 Block 區間起點 ----------
                                                                let start = new Date(clickedDate);
                                                                while (true) {
                                                                    const prev = new Date(start);
                                                                    prev.setDate(prev.getDate() - 1);
                                                                    const prevItem = monthDays.find(
                                                                        (d) => format(new Date(d.date), "yyyy-MM-dd") === format(prev, "yyyy-MM-dd")
                                                                    );
                                                                    if (!prevItem || prevItem.status !== "blocked") break;
                                                                    start = prev;
                                                                }

                                                                // ---------- 找出 Block 區間終點 ----------
                                                                let end = new Date(clickedDate);
                                                                while (true) {
                                                                    const next = new Date(end);
                                                                    next.setDate(next.getDate() + 1);
                                                                    const nextItem = monthDays.find(
                                                                        (d) => format(new Date(d.date), "yyyy-MM-dd") === format(next, "yyyy-MM-dd")
                                                                    );
                                                                    if (!nextItem || nextItem.status !== "blocked") break;
                                                                    end = next;
                                                                }

                                                                // ---------- 打開 Popup（Block Edit 模式） ----------
                                                                setPopupData({
                                                                    mode: "block-edit",
                                                                    listingId: listing.Id,
                                                                    startDate: format(start, "yyyy-MM-dd"),
                                                                    endDate: format(end, "yyyy-MM-dd"),
                                                                    listing: {
                                                                        listingId: listing.Id,
                                                                        name: listing.Name,
                                                                        kozyName: listing.KozyGuruName,
                                                                        bedrooms: listing.Bedrooms,
                                                                        bathrooms: listing.Bathrooms,
                                                                        guests: listing.Guests,
                                                                        image: listing.Thumbnail,
                                                                    },
                                                                    // 新增: 傳入 reservationMap
                                                                    reservationMap: reservationMap,
                                                                    hideBlockTab: true
                                                                });

                                                                return;
                                                            }


                                                            if (isBooked) {
                                                                setToastMessage("This date is already booked and cannot be edited.");
                                                                setShowToast(true);
                                                                setTimeout(() => setShowToast(false), 5000);
                                                                return;
                                                            }

                                                            if (!permissionChecks.canEditPrices) {
                                                                setToastMessage("You do not have permission to edit the price");
                                                                setShowToast(true);
                                                                setTimeout(() => setShowToast(false), 5000);
                                                                return;
                                                            }


                                                            setPopupData({
                                                                mode: "price",
                                                                listingId: listing.Id,
                                                                startDate: format(new Date(day.date), "yyyy-MM-dd"),
                                                                endDate: format(new Date(day.date), "yyyy-MM-dd"),
                                                                price: day.price,
                                                                currency: day.currency,
                                                                listing: {
                                                                    listingId: listing.Id,
                                                                    name: listing.Name,
                                                                    kozyName: listing.KozyGuruName,
                                                                    bedrooms: listing.Bedrooms,
                                                                    bathrooms: listing.Bathrooms,
                                                                    guests: listing.Guests,
                                                                    image: listing.Thumbnail,
                                                                },
                                                                reservationMap: reservationMap,
                                                                calendarData: calendarData,
                                                                hideBlockTab: true

                                                            });
                                                        } : undefined}


                                                        style={{
                                                            minWidth: 60,
                                                            maxWidth: 60,
                                                            height: 60,
                                                            flexShrink: 0,
                                                            padding: "6px 4px",
                                                            fontSize: "12px",
                                                            lineHeight: 1.1,
                                                            zIndex: 2,
                                                            position: "relative",
                                                            display: "flex",
                                                            flexDirection: "column",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            cursor: permissionChecks.canEditPrices && isFuture && !isBooked ? 'pointer' : 'default',
                                                            backgroundColor:
                                                                day.status === "blocked"
                                                                    ? "#e0e0e0"
                                                                    : isPast
                                                                        ? "#f6f6f6"
                                                                        : "#fff",
                                                            opacity: updatingDates.has(day.date) ? 0.6 : (!permissionChecks.canEditPrices ? 0.7 : 1),
                                                            transition: 'opacity 0.3s, background-color 0.3s',
                                                            animation: updatingDates.has(day.date) ? `${pulse.getName()} 1.5s ease-in-out infinite` : 'none'
                                                        }}
                                                    >

                                                        {/* 🆕 更新中的覆蓋層 */}
                                                        {updatingDates.has(day.date) && (
                                                            <UpdatingOverlay>
                                                                <CellSpinner />
                                                            </UpdatingOverlay>
                                                        )}
                                                        {/* 加上灰色斜線背景（只有過去未預訂才顯示） */}
                                                        {isPast && !isBooked && (
                                                            <div
                                                                style={{
                                                                    position: "absolute",
                                                                    inset: 0,
                                                                    backgroundImage:
                                                                        "repeating-linear-gradient(135deg, rgba(0,0,0,0.05) 0, rgba(0,0,0,0.05) 2px, transparent 2px, transparent 6px)",
                                                                    pointerEvents: "none",
                                                                    zIndex: 1,
                                                                }}
                                                            />
                                                        )}

                                                        {/* 價格顯示 */}
                                                        {day.status === "blocked" ? (
                                                            <DayCellBlockLabel>
                                                                BLOCK
                                                            </DayCellBlockLabel>
                                                        ) : (
                                                            !isBooked && (
                                                                <DayCellLabel>
                                                                    {permissionChecks.canViewPrices && day.price !== undefined && day.price !== null
                                                                        ? `${formatCurrencySymbol(day.currency)}${day.price}`
                                                                        : ''
                                                                    }
                                                                </DayCellLabel>
                                                            )
                                                        )}

                                                    </DayCell>
                                                );
                                            })}
                                        </div>

                                        <ReservationBlockLayer>
                                            {renderReservationBlocks(listing.Id)}
                                        </ReservationBlockLayer>

                                    </div>
                                );
                            })}
                        </div>
                    </CalendarScrollableArea>
                </ListingsWrapper>
            </CalendarWrapper>
            {popupData && (
                <PopupOverlay onClick={(e) => e.stopPropagation()}>
                    <PopupContent onClick={(e) => e.stopPropagation()}>
                        <PopUp
                            data={popupData}
                            onClose={() => {
                                // 🆕 只有在不是更新中才允許關閉
                                if (!isUpdatingPrice) {
                                    setPopupData(null);
                                }
                            }}
                            isLoading={isUpdatingPrice}
                            showToast={(message, isError = false) => {
                                setToastMessage(message);
                                setShowToast(true);
                                setTimeout(() => setShowToast(false), 5000);
                            }}
                            onSave={async ({ mode, listingId, startDate, endDate, newPrice, weekdays, weekdayPrices, bookedDates }) => {

                                if (!permissionChecks.canUseBlockFeature && (mode === "block" || mode === "block-edit")) {
                                    alert("The Block function has been hidden");
                                    return;
                                }
                                // 處理 Block Date（新增）
                                if (mode === "block") {
                                    const result = applyMockBlockDate(listingId, startDate, endDate);

                                    if (!result.success) {
                                        alert(result.message);
                                        return;
                                    }

                                    alert("Block Date saved!");
                                    setPopupData(null);
                                    return;
                                }

                                // 處理 Block Date Edit（編輯）
                                if (mode === "block-edit") {
                                    const result = applyMockBlockDate(listingId, startDate, endDate);

                                    if (!result.success) {
                                        alert(result.message);
                                        return;
                                    }

                                    alert("Block Date updated!");
                                    setPopupData(null);
                                    return;
                                }


                                // 處理 Price Edit
                                if (mode === "price") {
                                    console.log("=== Price Update Started ===");

                                    // 🆕 防止重複請求（雙重保護）
                                    if (isUpdatingPrice) {
                                        console.log("Processing, please do not click repeatedly");
                                        return;
                                    }


                                    if (!permissionChecks.canEditPrices) {
                                        alert("You don’t have permission to update the price");
                                        return;
                                    }

                                    // 🆕 設定 loading 狀態
                                    console.log("Updating price, setting loading state");
                                    setIsUpdatingPrice(true);

                                    try {
                                        // 🆕 呼叫實際 API 而不是只做本地更新
                                        const result = await updateCalendarPricesAPI(
                                            listingId,
                                            startDate,
                                            endDate,
                                            newPrice,
                                            weekdays,
                                            weekdayPrices,
                                            bookedDates
                                        );

                                        if (result.success) {
                                            // 🔍 更精確的計算實際更新的天數
                                            const start = new Date(startDate);
                                            const end = new Date(endDate);
                                            const daysDiff = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;

                                            console.log("實際日期範圍天數:", daysDiff);
                                            console.log("API 回報更新數量:", result.updatedCount);

                                            // 🔥 先關閉 popup
                                            setPopupData(null);

                                            // 🔥 顯示成功 Toast
                                            const msg = `Prices updated successfully: ${result.updatedCount} date${result.updatedCount > 1 ? 's' : ''} updated.\n\nPrices may take a few minutes to sync. Please refresh if not updated immediately.`;
                                            setToastMessage(msg);
                                            setShowToast(true);
                                            setTimeout(() => {
                                                setShowToast(false);
                                            }, 3000);
                                        } else {
                                            // 🔥 使用 Toast 而非 alert
                                            setToastMessage("Failed to update the price.");
                                            setShowToast(true);
                                            setTimeout(() => {
                                                setShowToast(false);
                                            }, 3000);
                                        }
                                    } catch (error) {
                                        console.error("Price update error:", error);

                                        // 🔥 使用 Toast 而非 alert
                                        const errMsg = `Price update failed: ${error.message}`;
                                        setToastMessage(errMsg);
                                        setShowToast(true);
                                        setTimeout(() => {
                                            setShowToast(false);
                                        }, 3000);
                                    } finally {
                                        // 🆕 無論成功失敗都要重置 loading 狀態
                                        setIsUpdatingPrice(false);
                                    }

                                    return;
                                }
                            }}
                        />
                    </PopupContent>
                </PopupOverlay>
            )}
            {/* 1. Toast 通知 - 參考 AdminDashboard 的樣式 */}
            {showToast && (
                <ToastContainer isError={toastMessage.includes('failed') || toastMessage.includes('valid price')}>
                    <div>{toastMessage}</div>
                    <ToastCloseButton
                        isError={toastMessage.includes('failed') || toastMessage.includes('valid price')}
                        onClick={() => setShowToast(false)}
                    >
                        ×
                    </ToastCloseButton>
                </ToastContainer>
            )}

            {/* 🆕 2. 右上角「Updating...」指示器 */}
            {isRefreshing && (
                <UpdateIndicator>
                    <SpinnerIcon />
                    Updating...
                </UpdateIndicator>
            )}


        </Container>
    )
}


export default Calendar;